import React from 'react';

/**
 * Cricket Refreshed brand mark.
 *
 * Variants:
 *   <Logo />                  full lockup: mark + wordmark
 *   <Logo mark />             just the geometric mark
 *   <Logo wordmark />         just the wordmark
 *
 * Sizing: pass `size` (px) for the mark height. Wordmark scales with it.
 * Theme-aware: wordmark color follows `currentColor`, the mark has its
 * own gradients so it pops on both light and dark backgrounds.
 */
export default function Logo({
  mark = false,
  wordmark = false,
  size = 32,
  className = '',
  showTagline = false,
}) {
  const onlyMark = mark && !wordmark;
  const onlyWord = wordmark && !mark;
  const both = !onlyMark && !onlyWord;

  return (
    <span
      className={`inline-flex items-center gap-2 select-none ${className}`}
      style={{ height: size }}
    >
      {(onlyMark || both) && <Mark size={size} />}
      {(onlyWord || both) && (
        <span className="flex flex-col leading-none">
          <span
            className="font-extrabold tracking-tight"
            style={{
              fontSize: size * 0.62,
              fontFamily: 'Outfit, Inter, system-ui, sans-serif',
              letterSpacing: '-0.02em',
            }}
          >
            Cricket<span style={{ color: '#00C9A7' }}>·</span>Refreshed
          </span>
          {showTagline && (
            <span
              className="opacity-60 mt-1"
              style={{
                fontSize: size * 0.28,
                fontFamily: 'Inter, system-ui, sans-serif',
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
              }}
            >
              From the backyard net to the world cup
            </span>
          )}
        </span>
      )}
    </span>
  );
}

function Mark({ size }) {
  return (
    <svg
      viewBox="0 0 64 64"
      width={size}
      height={size}
      role="img"
      aria-label="Cricket Refreshed"
    >
      <defs>
        <linearGradient id="cr-arc-c" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#00A651" />
          <stop offset="55%" stopColor="#00C9A7" />
          <stop offset="100%" stopColor="#00D9FF" />
        </linearGradient>
        <linearGradient id="cr-bat-c" x1="0" y1="1" x2="1" y2="0">
          <stop offset="0%" stopColor="#1E2A44" />
          <stop offset="100%" stopColor="#3B4A6B" />
        </linearGradient>
        <radialGradient id="cr-ball-c" cx="0.35" cy="0.35" r="0.8">
          <stop offset="0%" stopColor="#FF8A5C" />
          <stop offset="100%" stopColor="#FF4F2B" />
        </radialGradient>
      </defs>

      <path
        d="M 52 18 A 24 24 0 1 1 32 8"
        fill="none"
        stroke="url(#cr-arc-c)"
        strokeWidth="6"
        strokeLinecap="round"
      />
      <path d="M 30 2 L 32 8 L 26 11 Z" fill="url(#cr-arc-c)" />

      <g transform="rotate(-32 32 32)">
        <rect x="30" y="14" width="4.4" height="10" rx="1.2" fill="url(#cr-bat-c)" />
        <rect x="28.4" y="24" width="7.6" height="22" rx="2.4" fill="url(#cr-bat-c)" />
      </g>

      <circle cx="42" cy="42" r="7.2" fill="url(#cr-ball-c)" />
      <path
        d="M 36.5 41.5 Q 42 45 47.5 41.5"
        fill="none"
        stroke="#FFFFFF"
        strokeWidth="0.9"
        strokeLinecap="round"
        strokeDasharray="1.4 1.6"
        opacity="0.95"
      />
    </svg>
  );
}
